import styles from './index.module.css'

import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { getTrackWaveform } from '@disco/state-management/actions/MediaActions'
import { getTrackWaveformStatus } from '@disco/state-management/selectors/media'

import { Flex } from 'components/ds'
import { SearchColumnProps } from 'components/search/search-columns/types'
import Skeleton from 'components/ui/skeleton'

import OptimizedWaveform from './OptimizedWaveform'

export const WaveformColumn = React.memo(
  ({ info }: SearchColumnProps) => {
    const dispatch = useDispatch()
    const trackId = parseInt(info.row.id)
    const track = info.row.original
    const rowIndex = info.row.index // Get row index from table
    const [showSkeleton, setShowSkeleton] = useState(true)
    const [isCanvasDrawn, setIsCanvasDrawn] = useState(false)

    // Get tracklist from table meta (passed from SearchPage via DataTable)
    const tracklist = (info.table.options.meta as { tracklist?: number[] })
      ?.tracklist

    // Get waveform status to check if already loading/loaded (prevents duplicate dispatches)
    const waveformStatus = useSelector(
      state => getTrackWaveformStatus(state, { trackId }),
      // Prevent re-renders if only unrelated state changes
      (prev, next) =>
        prev?.waveformData === next?.waveformData &&
        prev?.waveformLoadStatus === next?.waveformLoadStatus,
    )

    const parsedWaveformData = useMemo(() => {
      if (!track?.waveform && !waveformStatus?.waveformData) {
        return null
      }

      // Prioritize waveformStatus.waveformData (already parsed, more readily available)
      if (waveformStatus?.waveformData) {
        return waveformStatus.waveformData
      }

      // Fall back to track.waveform (might need JSON parsing)
      if (track?.waveform) {
        try {
          if (typeof track.waveform === 'string') {
            return JSON.parse(track.waveform)
          } else {
            return track.waveform // Already parsed
          }
        } catch (e) {
          console.error('Waveform parsing error:', e)
          return null
        }
      }

      return null
    }, [track?.waveform, waveformStatus?.waveformData])

    // Check if track should have a waveform (has duration > 0)
    const shouldShowWaveform = Boolean(track?.duration && track.duration > 0)

    // Load waveform with staggered timing to prevent UI freeze
    useEffect(() => {
      if (track && !track.waveform && !waveformStatus && shouldShowWaveform) {
        // Calculate relative position within current page/batch
        const PAGE_SIZE = 50
        const VISIBLE_ROWS = 12
        const relativeRowIndex = rowIndex % PAGE_SIZE // Reset index for each new page

        if (relativeRowIndex < VISIBLE_ROWS) {
          // Priority rows: dispatch immediately
          dispatch(getTrackWaveform(trackId))
        } else {
          // Non-priority rows: small staggered delay to prevent UI freeze
          const delay = (relativeRowIndex - VISIBLE_ROWS) * 1 // 1ms, 2ms, 3ms..
          const timeoutId = setTimeout(() => {
            dispatch(getTrackWaveform(trackId))
          }, delay)

          return () => clearTimeout(timeoutId)
        }
      }
    }, [track, trackId, waveformStatus, rowIndex, dispatch, shouldShowWaveform])

    useEffect(() => {
      // Show skeleton for tracks with duration that don't have waveform data yet
      if (waveformStatus?.waveformLoadStatus === 'loading') return
      const skeletonDelay = 500 // Keep skeleton for an additional 500ms after load to prevent flicker
      const timeoutId = setTimeout(() => {
        setShowSkeleton(
          shouldShowWaveform && !parsedWaveformData && !isCanvasDrawn,
        )
      }, skeletonDelay)
      return () => clearTimeout(timeoutId)
    }, [
      waveformStatus?.waveformLoadStatus,
      parsedWaveformData,
      shouldShowWaveform,
      isCanvasDrawn,
    ])

    return (
      <Flex
        height="40px"
        align="center"
        width={`calc(var(--col-${String(info.column.id)
          .toLowerCase()
          .replace(/\s+/g, '-')}-size) * 1px)`}
        overflow="hidden"
      >
        {showSkeleton ? (
          <Flex
            width={`calc(var(--col-${String(info.column.id)
              .toLowerCase()
              .replace(/\s+/g, '-')}-size) * 1px)`}
            height="25px"
          >
            <Skeleton type="block" />
          </Flex>
        ) : (
          <>
            {parsedWaveformData ? (
              <OptimizedWaveform
                className={styles.Waveform}
                track={track}
                parsedWaveformData={parsedWaveformData}
                barGap={2}
                barWidth={1}
                isHoverSeekEnabled
                height={20}
                waveformLocation="Search page"
                tracklist={tracklist}
                noAnimation
                onIsCanvasDrawn={setIsCanvasDrawn}
              />
            ) : (
              // Show blank space for tracks without duration or failed waveforms

              <Flex width="400px" height="20px" />
            )}
          </>
        )}
      </Flex>
    )
  },
  // Custom comparison — if column width changes
  (prev, next) =>
    prev?.info.column.getSize() === next?.info.column.getSize() &&
    prev?.info.column.getIsResizing() === next?.info.column.getIsResizing() &&
    prev?.info.row.id === next?.info.row.id &&
    prev?.info.getValue() === next?.info.getValue(),
)

WaveformColumn.displayName = 'WaveformColumn'
